import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '../global/contexts/auth/auth';
import { api as apiConfig, features } from '../global/config/api';
import { IconGithub } from '../common/icons';

import mind from '../assets/mind.webp';

import './styles/login.css';

// TSS vite/05 — login flow. Backend sets the httpOnly session cookie
// + csrf_token cookie on a successful POST /auth/login.
//
// The credential is EMAIL, not username: mastermind_core validates the body
// against the `login` schema in data/schemas/user.js, which requires
// { email, password }.
//
// Layout taken from design/referencias/ia_login.webp: a dark frame split in
// two, the story on the left, the form card on the right. Every color comes
// from the --mm-night-* tokens (design/paleta.md §4).

const GREETING = 'Hola dev, ¿qué idea loca tienes hoy?';

// Starts once the console has finished sliding in, so the line is not typed
// while the block is still moving. Tied to the console's animation-delay in
// login.css — move one, move the other.
const TYPING_START_MS = 850;
const TYPING_STEP_MS = 45;

function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

// OAuth is a full page navigation and not an XHR: the browser has to land on
// GitHub, come back to the backend's callback, and only then return here. That
// is also why nothing else is needed on this side — the backend sets the same
// auth_token + csrf_token cookies as a password login, and AuthProvider picks
// the session up from /auth/me on the next boot.
//
// Gated by features.githubLogin (global/config/api.js). The endpoint does not
// exist in mastermind_core yet.
function startGithubLogin() {
    window.location.assign(`${apiConfig.endpoint}/auth/github`);
}

// Types the text out one character at a time. With reduced motion on, nothing
// is typed: the full line is there from the first paint.
function useTypewriter(text) {
    // The reduced-motion case is resolved in the initial state and not inside
    // the effect: a synchronous setState there triggers cascading renders, and
    // the template's lint rules flag it as an error.
    const [typed, setTyped] = useState(() => (prefersReducedMotion() ? text : ''));

    useEffect(() => {
        if (prefersReducedMotion()) return undefined;

        let index = 0;
        let timer;
        const start = setTimeout(() => {
            timer = setInterval(() => {
                index += 1;
                setTyped(text.slice(0, index));
                if (index >= text.length) clearInterval(timer);
            }, TYPING_STEP_MS);
        }, TYPING_START_MS);

        return () => {
            clearTimeout(start);
            clearInterval(timer);
        };
    }, [text]);

    return typed;
}

export default function LoginPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { login, isAuthenticated } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const greeting = useTypewriter(GREETING);

    useEffect(() => {
        if (isAuthenticated) navigate('/admin/clients', { replace: true });
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (params.get('logout') === 'true') toast.success('Sesión cerrada');
    }, [params]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setSubmitting(true);
        try {
            await login({ email, password });
            navigate('/admin/clients', { replace: true });
        } catch (err) {
            toast.error(err?.message || 'Credenciales inválidas');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login">
            <div className="login__frame">

                <section className="login__hero">
                    {/* Decorative: the headline already carries the message, so
                        an alt here would only be noise for a screen reader.
                        Source file is design/referencias/image.png. */}
                    <img className="login__art" src={mind} alt="" aria-hidden="true" />

                    <div className="login__brand">
                        <span className="login__brand-text">
                            <span className="login__brand-company">Trianametría</span>
                            <span className="login__brand-product">Mastermind</span>
                        </span>
                    </div>

                    <h1 className="login__headline">
                        <span className="login__headline-outline">Vamos a</span>
                        <span className="login__headline-solid">cambiar</span>
                        <span className="login__headline-solid">el mundo.</span>
                    </h1>

                    {/* Decorative, and its text changes on every keystroke: a
                        screen reader would announce the same line thirty-six
                        times. Hidden as a whole. */}
                    <div className="login__console" aria-hidden="true">
                        <span className="login__console-prompt">$</span>
                        <span className="login__console-text">{greeting}</span>
                        <span className="login__console-caret" />
                    </div>
                </section>

                <form className="login__panel" onSubmit={handleSubmit}>
                    <h2 className="login__title">Empecemos.</h2>
                    <p className="login__subtitle">
                        Entrá con tu cuenta y seguimos donde lo dejamos.
                    </p>

                    <label className="login__field">
                        <span className="login__label">Correo</span>
                        <input
                            className="login__input"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="vos@trianametria.com"
                            autoComplete="username"
                            required
                        />
                    </label>

                    <label className="login__field">
                        <span className="login__label">Contraseña</span>
                        <input
                            className="login__input"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                        />
                    </label>

                    {features.githubLogin && (
                        <>
                            <div className="login__divider">
                                <span className="login__divider-text">o</span>
                            </div>

                            <button
                                type="button"
                                className="login__provider"
                                onClick={startGithubLogin}
                            >
                                <IconGithub />
                                Continuar con GitHub
                            </button>
                        </>
                    )}

                    <div className="login__actions">
                        <span className="login__hint">
                            ¿No entrás? Te abre la puerta quien administra.
                        </span>

                        <button
                            type="submit"
                            className="login__submit"
                            disabled={submitting || !email || !password}
                        >
                            {submitting ? 'Entrando' : 'Entrar'}
                            <span className="login__submit-arrow" aria-hidden="true">→</span>
                        </button>
                    </div>
                </form>

            </div>
        </div>
    );
}
